import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import {
  UserDTO,
  NameUtils,
  AppConfig,
  EconsultUtils,
  EconsultUserSettings,
  NoteStatusCodes,
  NoteActionTypes,
  CaseNote,
  NoteTypes,
  UserSource
} from '@oh-vcp/components-common';
import {
  Avatar,
  Button,
  Menu,
  MenuItem,
  Tooltip,
  Stack,
  Toolbar,
  Link as MuiLink,
  Alert,
  Box,
  Typography,
  styled,
  useMediaQuery,
  SvgIcon
} from '@mui/material';
// eslint-disable-next-line import/no-extraneous-dependencies
import {
  AppNavMenuSelector,
  CircleQuestion,
  Cog,
  Comments,
  DisplayDesktop,
  Envelope,
  FileMagnifyingGlass,
  FileWaveform,
  FlexGrow,
  HelpIcon,
  HospitalIcon,
  LanguageSelectorContainer,
  Lock,
  Logo,
  NavBar,
  NavBarPaddedContainer,
  NotificationCount,
  NotificationDot,
  OHTheme,
  OntarioHealthLogo,
  PeopleGroup,
  QuestionCircleSVGIcon,
  RedirectService,
  ResponsiveFlexContainer,
  SquareCheck,
  SquareSlidersVertical,
  UserEdit,
  UserPen,
  UsersGear,
  Favourites,
  StarSVGIcon,
  User11Icon,
  User12Icon,
  User2Icon,
  UserSmBlueSVGIcon,
  GroupSmSVGIcon,
  ChatSvgIcon,
  NoFavouritesIcon,
  FavouriteGreyIcon
} from '@oh-vcp/components-ui';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import ListItemIcon from '@mui/material/ListItemIcon';
import { Login, Logout, PersonOutline, ArrowDropDown, ArrowDropUp } from '@mui/icons-material';
import { useTheme } from '@mui/system';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import EconsultCompleteCaseDialog from '../econsultCompleteCaseDialog/EconsultCompleteCaseDialog';
import EconsultDraftLostWarningDialog from '../econsultDraftLostWarningDialog/EconsultDraftLostWarningDialog';
import { useEconsultCaseDetailsContext } from '../econsultCaseDetailsContext/EconsultCaseDetailsContext';
import EconsultUserService from '../services/econsultUserService';
import CaseService from '../services/caseService';
import useRssFeed from '../services/useRssFeed';
import { useAppNavBarContext } from '../appNavBarContext/AppNavBarContext';
import useManagedProfilesPolicy from '../services/useManagedProfilesPolicy';
import useSuggestPendingCount from '../services/useSuggestPendingCount';
import useAllSuggestUpdates from '../services/useAllSuggestUpdates';
import useFavourites from '../services/useFavourites';
import EconsultFavRequestConsult from '../econsultFavRequestConsult/EconsultFavRequestConsult';
import RenameFavouriteDialog from '../renameFavouriteDialog/RenameFavouriteDialog';
import RemoveFavouriteDialog from '../removeFavouriteDialog/RemoveFavouriteDialog';

interface EconsultTopNavigationProps {
  user: UserDTO | null;
  hideProfileOption?: boolean;
  userSettings?: EconsultUserSettings;
}

const QuickLink = styled(MuiLink)(({ theme }) => ({
  textDecoration: 'none',
  color: theme.palette.base.grey4,
  cursor: 'pointer',
  '&:hover': {
    color: theme.palette.primary.custom3
  }
}));

const QuickLinkMenuItem = styled(MenuItem)(({ theme }) => ({
  '&:hover': {
    backgroundColor: 'transparent',
    '& .MuiListItemIcon-root': {
      color: theme.palette.primary.custom3,
      '& svg': {
        color: theme.palette.primary.custom3,
        fill: theme.palette.primary.custom3,
        stroke: theme.palette.primary.custom3
      }
    }
  }
}));

const FavMenuItem = styled(MenuItem)(({ theme }) => ({
  '&:hover': {
    backgroundColor: theme.palette.base.grey6
  },
  height: 50
}));

const FavItemPanel = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  padding: '10px 30px',
  height: 50,
  '& svg': {
    cursor: 'pointer',
    color: 'white',
    fill: 'white',
    stroke: 'white'
  }
}));

const FavLabel = styled(Typography)({
  textOverflow: 'ellipsis',
  overflow: 'hidden',
  whiteSpace: 'nowrap'
});

const EconsultTopNavigation: FC<EconsultTopNavigationProps> = ({
  user,
  hideProfileOption,
  userSettings
}) => {
  const navigate = useNavigate();
  const { data: feedData } = useRssFeed('help-econsult');
  const theme = useTheme();
  const [isCaseCompleteDialogOpen, setIsCaseCompleteDialogOpen] = useState<boolean>(false);
  const [isDraftWarningDialogOpen, setIsDraftWarningDialogOpen] = useState<boolean>(false);
  const {
    caseDetails,
    userRole,
    setCaseDetails,
    draftCaseOrNoteChanged,
    setNavigationAction,
    fhirPatientGuid,
    fhirContext
  } = useEconsultCaseDetailsContext();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [helpMenuAnchorEl, setHelpMenuAnchorEl] = React.useState<null | HTMLElement>(null);
  const [favMenuAnchorEl, setFavMenuAnchorEl] = React.useState<null | HTMLElement>(null);
  const [favSubMenuAnchorEls, setFavSubMenuAnchorEls] = useState<Map<number, HTMLElement>>(
    new Map()
  );
  const favMenuAnchorElRef = useRef<HTMLElement | undefined>();
  const favMenuAnchorElRefBak = useRef<HTMLElement | undefined>();
  const [selectedFavouriteId, setSelectedFavouriteId] = useState<number | undefined>(undefined);
  const [favToRenameId, setFavToRenameId] = useState<number | undefined>(undefined);
  const [favToRenameDisplayName, setFavToRenameDisplayName] = useState<string | undefined>(
    undefined
  );
  const [favToRenameFavouriteName, setFavToRenameFavouriteName] = useState<string | undefined>(
    undefined
  );
  const [favToRemoveId, setFavToRemoveId] = useState<number | undefined>(undefined);
  const [favToRemoveDisplayName, setFavToRemoveDisplayName] = useState<string | undefined>(
    undefined
  );
  const [favToRemoveFavouriteName, setFavToRemoveFavouriteName] = useState<string | undefined>(
    undefined
  );
  const open = Boolean(anchorEl);
  const isHelpMenuOpen = Boolean(helpMenuAnchorEl);
  const isFavMenuOpen = Boolean(favMenuAnchorEl);
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const hasEconsultDashboardAccess = useMemo(() => {
    return user?.groups?.find((g) => g === 'econsult_dashboard');
  }, [user]);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const { isDrawerOpen, setIsDrawerOpen } = useAppNavBarContext();
  const { data: managedProfilesPolicy } = useManagedProfilesPolicy(Boolean(user));
  const { data: suggestPendingCount } = useSuggestPendingCount('people', user?.contactid);
  const { data: suggestDelegatorCount } = useAllSuggestUpdates(Boolean(user));
  const { data: favouriteContacts } = useFavourites(Boolean(user));
  const actionRequired = useMemo(() => {
    if (suggestPendingCount && suggestPendingCount.count && suggestPendingCount.count > 0) {
      return true;
    }
    if (!managedProfilesPolicy?.canManageProfiles) return false;
    const pending = suggestDelegatorCount?.find(
      (d) => d.pendingSuggestUpdateCount && d.pendingSuggestUpdateCount > 0
    );
    if (pending) return true;
    return false;
  }, [suggestPendingCount, suggestDelegatorCount, managedProfilesPolicy]);
  const favIconStyle: React.CSSProperties = {
    height: 15,
    width: 15,
    position: 'relative',
    top: 2
  };

  const handleHelpMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setHelpMenuAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const handleHelpMenuClose = () => {
    setHelpMenuAnchorEl(null);
  };
  const handleFavMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setFavMenuAnchorEl(event.currentTarget);
    favMenuAnchorElRef.current = undefined;
    favMenuAnchorElRefBak.current = event.currentTarget;
  };
  const handleFavMenuClose = () => {
    if (!favMenuAnchorElRef.current) {
      setSelectedFavouriteId(undefined);
      setFavMenuAnchorEl(null);
      setFavSubMenuAnchorEls(new Map());
    } else {
      favMenuAnchorElRef.current = undefined;
    }
  };
  const keepFavMenuOpen = () => {
    if (favMenuAnchorElRefBak.current) {
      favMenuAnchorElRef.current = favMenuAnchorElRefBak.current;
    }
    if (favMenuAnchorElRef.current) {
      setFavMenuAnchorEl(favMenuAnchorElRef.current);
    }
  };
  const clearFavMenuElRef = () => {
    favMenuAnchorElRef.current = undefined;
  };
  const handleFavSubMenuClose = (id: number) => {
    setFavSubMenuAnchorEls((prev) => {
      const nextMap = new Map(prev);
      if (nextMap.has(id)) {
        nextMap.delete(id);
      }
      return nextMap;
    });
  };
  const handleFavSubMenuClick = (id: number) => {
    setFavSubMenuAnchorEls((prev) => {
      const nextMap = new Map(prev);
      if (nextMap.has(id)) {
        nextMap.delete(id);
      } else {
        const el = document.getElementById(`fav_${id}`);
        if (el) {
          nextMap.set(id, el);
        }
      }
      return nextMap;
    });
  };
  const handleFavClicked = (id: number) => {
    if (selectedFavouriteId === id) {
      setSelectedFavouriteId(undefined);
    } else {
      setSelectedFavouriteId(id);
    }
  };
  const handleLogout = () => {
    if (
      caseDetails &&
      userRole &&
      EconsultUserService.isReferrerResolveCompletedConsultAction(caseDetails, userRole)
    ) {
      setIsCaseCompleteDialogOpen(true);
    } else if (
      caseDetails &&
      caseDetails.caseId === EconsultUtils.getCaseIdOnCurrentPage() &&
      draftCaseOrNoteChanged
    ) {
      setIsDraftWarningDialogOpen(true);
    } else {
      navigate(`/logout`);
    }
  };
  const onCompleteDialogCancelled = () => {
    setIsCaseCompleteDialogOpen(false);
    navigate(`/logout`);
  };
  const onCompleteDialogCompleted = async () => {
    setIsCaseCompleteDialogOpen(false);
    if (!caseDetails) return;

    const draftFinishNote = caseDetails?.notes?.find(
      (n) => n.statusCd === NoteStatusCodes.draft && n.actionTypeCd === NoteActionTypes.finish
    );
    if (!draftFinishNote) {
      const newNote: CaseNote = {
        actionTypeCd: NoteActionTypes.finish,
        caseId: caseDetails?.caseId,
        noteTypeCd: NoteTypes.referrer,
        ownerId: user?.userid,
        statusCd: NoteStatusCodes.draft
      };
      await CaseService.upsertCaseNote(newNote);
      setCaseDetails({
        ...caseDetails,
        notes: [...(caseDetails.notes ? caseDetails.notes : []), newNote]
      });
    }
    setNavigationAction({
      navigateToUrl: '/logout'
    });
  };
  const onDraftWarningDialogCancelled = () => {
    setIsDraftWarningDialogOpen(false);
    navigate(`/logout`);
  };
  const onDraftWarningDialogConfirmed = () => {
    setIsDraftWarningDialogOpen(false);
  };
  const handleEdit = () => {
    RedirectService.redirectToPeopleProfile(`${user?.userid}`);
  };
  const { t } = useTranslation();

  const visibleStyle = {
    display: 'block'
  };
  const invisibleStyle = {
    display: 'none'
  };
  const [alertVisibility, setAlertVisibility] = useState(invisibleStyle);
  useEffect(() => {
    if (userSettings) {
      if (userSettings.eConsult) {
        const userUnavailable = userSettings.eConsult.filter(
          (setting: { name: string }) => setting.name === 'UNAVAILABILITY'
        );
        if (
          EconsultUtils.getCurrentOutOfOfficeDate(
            `${userUnavailable && userUnavailable.length > 0 ? userUnavailable[0].value : ''}`
          )
        ) {
          setAlertVisibility(visibleStyle);
          console.log('User is unavailable'); // eslint-disable-line no-console
        }
      }
    }
  }, [userSettings]);

  const handleMobileMenuClicked = () => {
    setIsDrawerOpen(!isDrawerOpen);
  };

  const handleFavProfileClicked = (
    id: string | number | undefined,
    category: string | undefined
  ) => {
    if (!id || !category) return;
    if (category === 'CONTACT') {
      RedirectService.redirectToDirectoryPeopleProfile(id?.toString());
    } else if (category === 'PROGRAM') {
      RedirectService.redirectToProgramProfile(id?.toString());
    }
  };

  const handleFavRenameClicked = (
    id: number,
    displayName: string | undefined,
    favouriteName: string | undefined
  ) => {
    setFavSubMenuAnchorEls(new Map());
    setFavToRenameId(id);
    setFavToRenameDisplayName(displayName);
    setFavToRenameFavouriteName(favouriteName);
  };

  const handleRenameFavDialogClosed = () => {
    setFavToRenameId(undefined);
    setFavToRenameDisplayName(undefined);
    setFavToRenameFavouriteName(undefined);
  };

  const handleFavRemoveClicked = (
    id: number,
    displayName: string | undefined,
    favouriteName: string | undefined
  ) => {
    setFavSubMenuAnchorEls(new Map());
    setFavToRemoveId(id);
    setFavToRemoveDisplayName(displayName);
    setFavToRemoveFavouriteName(favouriteName);
  };

  const handleRemoveFavDialogClosed = () => {
    setFavToRemoveId(undefined);
    setFavToRemoveDisplayName(undefined);
    setFavToRemoveFavouriteName(undefined);
  };
  const trimOrgName = (orgName: string | undefined) => {
    if (orgName && orgName.length > 44) {
      const pos = orgName.lastIndexOf(' ', 42);
      if (pos > 36) {
        return [orgName.substring(0, pos), orgName.substring(pos)];
      }
      return [orgName.substring(0, 44), orgName.substring(44)];
    }
    return [orgName];
  };
  const userOrg = isMobile ? trimOrgName(user?.organization) : [user?.organization];
  const showProfile = !!(user?.publishedProfile && !hideProfileOption);

  return (
    <>
      <NavBar position="sticky" about="NavBar">
        <NavBarPaddedContainer about="DisplayDesktopPaddedContainer">
          <Toolbar variant="dense" disableGutters about="Toolbar">
            <FlexGrow sx={{ pt: 1 }} about="FlexGrow">
              <Stack direction="row">
                <Link to="/" aria-label="Ontario Health logo">
                  {/* TODO use mui Link */}
                  <Logo
                    component={OntarioHealthLogo}
                    inheritViewBox
                    sx={{ width: isMobile ? 100 : 150, marginRight: isMobile ? 1 : 0 }}
                  />
                </Link>
                {isMobile && user?.source !== UserSource.SmartOnFhir && (
                  <AppNavMenuSelector onMenuClicked={handleMobileMenuClicked} />
                )}
              </Stack>
            </FlexGrow>
            <Stack direction="row" alignItems="center" marginRight={0}>
              <LanguageSelectorContainer>
                {/* <FlexGrow>
                    <LanguageSelector />
                  </FlexGrow> */}
                <Tooltip title={t('Case.navigation.helpAndSupport')} onClick={handleHelpMenuClick}>
                  <Avatar sx={{ bgcolor: OHTheme.palette.base.grey6, cursor: 'pointer' }}>
                    <HelpIcon component={QuestionCircleSVGIcon} viewBox="-7 -5 25 25" />
                  </Avatar>
                </Tooltip>
                <Menu
                  anchorEl={helpMenuAnchorEl}
                  id="account-menu"
                  open={isHelpMenuOpen}
                  onClose={handleHelpMenuClose}
                  onClick={handleHelpMenuClose}
                  transformOrigin={{ horizontal: 'center', vertical: 'top' }}
                  anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
                  slotProps={{
                    paper: {
                      sx: { minWidth: EconsultUtils.isMobileDevice() ? 0 : 400, minHeight: 200 }
                    }
                  }}>
                  <QuickLinkMenuItem>
                    <ListItemIcon>
                      <UserEdit fontSize="small" />
                    </ListItemIcon>
                    <QuickLink
                      href="https://dropbox.otn.ca/sites/default/files/public/privacy-dropbox/862/otnhubprivacystatement.pdf"
                      target="_blank">
                      <Typography variant="body2">{t('Case.menu.privacy')}</Typography>
                    </QuickLink>
                  </QuickLinkMenuItem>
                  <QuickLinkMenuItem>
                    <ListItemIcon>
                      <CircleQuestion fontSize="small" />
                    </ListItemIcon>
                    <QuickLink href={AppConfig.helpUrl} target="_blank">
                      <Typography variant="body2">{t('Case.menu.econsultHelp')}</Typography>
                    </QuickLink>
                  </QuickLinkMenuItem>
                  <QuickLinkMenuItem>
                    <ListItemIcon>
                      <Envelope fontSize="small" />
                    </ListItemIcon>
                    <QuickLink href="https://otn.ca/contact/" target="_blank">
                      <Typography variant="body2">{t('Case.menu.contactUs')}</Typography>
                    </QuickLink>
                  </QuickLinkMenuItem>
                  <QuickLinkMenuItem>
                    <ListItemIcon>
                      <Comments fontSize="small" />
                    </ListItemIcon>
                    <QuickLink href="https://forms.office.com/r/BkS2sE2uDG" target="_blank">
                      <Typography variant="body2">{t('Case.menu.feedback')}</Typography>
                    </QuickLink>
                  </QuickLinkMenuItem>
                  <QuickLinkMenuItem>
                    <ListItemIcon>
                      <Lock fontSize="small" />
                    </ListItemIcon>
                    <QuickLink
                      href="https://dropbox.otn.ca/privacy/otnhub-user-agreement.pdf"
                      target="_blank">
                      <Typography variant="body2">{t('Case.menu.agreements')}</Typography>
                    </QuickLink>
                  </QuickLinkMenuItem>
                  <Typography
                    variant="h3"
                    sx={{ fontWeight: 500, pl: 1.8, mt: 2 }}
                    color="primary.main">
                    {t('Case.navigation.quickLinks')}
                  </Typography>
                  {feedData &&
                    feedData.feed &&
                    feedData.feed.items.map((f) => (
                      <QuickLinkMenuItem>
                        <QuickLink href={f.link} target="_blank">
                          <Typography variant="body2">{f.title}</Typography>
                        </QuickLink>
                      </QuickLinkMenuItem>
                    ))}
                </Menu>
                {user?.source !== UserSource.SmartOnFhir && (
                  <>
                    <Tooltip title={t('Case.navigation.favourites')} onClick={handleFavMenuClick}>
                      <Avatar sx={{ bgcolor: OHTheme.palette.base.grey6, cursor: 'pointer' }}>
                        <SvgIcon component={StarSVGIcon} viewBox="-2 -2 25 25" />
                      </Avatar>
                    </Tooltip>
                    <Menu
                      anchorEl={favMenuAnchorEl}
                      id="account-menu"
                      open={isFavMenuOpen}
                      onClose={handleFavMenuClose}
                      onClick={clearFavMenuElRef}
                      transformOrigin={{ horizontal: 'center', vertical: 'top' }}
                      anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
                      slotProps={{
                        paper: {
                          sx: { minWidth: EconsultUtils.isMobileDevice() ? 0 : 400, minHeight: 200 }
                        }
                      }}>
                      {(!favouriteContacts || favouriteContacts.length === 0) && (
                        <Box sx={{ textAlign: 'center', pt: 1 }}>
                          <img src={NoFavouritesIcon} alt={NoFavouritesIcon} />
                          <Typography variant="h4" color="base.grey3">
                            {t('Fav.error.noFavourites')}
                          </Typography>
                          <Typography mt={2}>
                            {t('Fav.error.searchThe')}{' '}
                            <MuiLink sx={{ textDecoration: 'none' }} href={AppConfig.baseHubUrl}>
                              {t('Fav.error.directory')}
                            </MuiLink>{' '}
                            {t('Fav.error.and')}
                          </Typography>
                          <Typography>
                            {t('Fav.error.use')}{' '}
                            <img
                              src={FavouriteGreyIcon}
                              alt={FavouriteGreyIcon}
                              style={favIconStyle}
                            />{' '}
                            {t('Fav.error.toAddToFav')}
                          </Typography>
                        </Box>
                      )}
                      {favouriteContacts?.map((f) => (
                        <>
                          <FavMenuItem onClick={() => handleFavClicked(f.favouriteId)}>
                            <ListItemIcon>
                              {f.category?.toLowerCase() === 'program' ? (
                                <GroupSmSVGIcon fontSize="small" />
                              ) : (
                                <User11Icon fontSize="small" />
                              )}
                            </ListItemIcon>
                            <Stack direction="column" sx={{ color: theme.palette.base.grey4 }}>
                              <FavLabel variant="body2" sx={{ maxWidth: isMobile ? 200 : 300 }}>
                                {f.displayName}
                              </FavLabel>
                              <FavLabel variant="body3" sx={{ maxWidth: isMobile ? 200 : 300 }}>
                                {f.displayNameAdd}
                              </FavLabel>
                            </Stack>
                          </FavMenuItem>
                          {selectedFavouriteId === f.favouriteId && (
                            <FavItemPanel>
                              <Stack direction="row" justifyContent="space-between">
                                <Box>
                                  {(f.accepteConsult === 'YES' ||
                                    f.programAcceptEconsult === 'YES') && (
                                    <EconsultFavRequestConsult
                                      category={f.category || ''}
                                      programId={f.programId}
                                      programStatus={f.programStatus}
                                      programReferrers={f.programReferrers}
                                      userId={f.pcvcUserId}
                                      onActionComplete={handleFavMenuClose}
                                      keepFavMenuOpen={keepFavMenuOpen}
                                      unavailability={f.unavailability}
                                    />
                                  )}
                                </Box>
                                <MoreHorizIcon
                                  id={`fav_${f.favouriteId}`}
                                  onClick={() => handleFavSubMenuClick(f.favouriteId)}
                                />
                                <Menu
                                  anchorEl={favSubMenuAnchorEls.get(f.favouriteId)}
                                  open={favSubMenuAnchorEls.has(f.favouriteId)}
                                  onClose={() => handleFavSubMenuClose(f.favouriteId)}>
                                  <MenuItem
                                    onClick={() =>
                                      handleFavProfileClicked(
                                        f.category === 'CONTACT' ? f.contactId : f.programId,
                                        f.category
                                      )
                                    }>
                                    {t('Fav.submenu.profile')}
                                  </MenuItem>
                                  <MenuItem
                                    onClick={() =>
                                      handleFavRenameClicked(
                                        f.favouriteId,
                                        f.displayName,
                                        f.favouriteName
                                      )
                                    }>
                                    {t('Fav.submenu.rename')}
                                  </MenuItem>
                                  <MenuItem
                                    onClick={() =>
                                      handleFavRemoveClicked(
                                        f.favouriteId,
                                        f.displayName,
                                        f.favouriteName
                                      )
                                    }>
                                    {t('Fav.submenu.remove')}
                                  </MenuItem>
                                </Menu>
                              </Stack>
                            </FavItemPanel>
                          )}
                        </>
                      ))}
                    </Menu>
                  </>
                )}
              </LanguageSelectorContainer>
            </Stack>

            <ResponsiveFlexContainer ml={EconsultUtils.isMobileDevice() ? 0 : 5}>
              <Stack direction="row" alignItems="center">
                <Tooltip title={`${user?.firstName} ${user?.lastName}`} onClick={handleClick}>
                  <Avatar sx={{ bgcolor: OHTheme.palette.primary.main }}>
                    {NameUtils.makeNameInitials(user?.lastName, user?.firstName)}
                  </Avatar>
                </Tooltip>
                {actionRequired && (
                  <Box ml={0.5}>
                    <NotificationDot />
                  </Box>
                )}
                <DisplayDesktop>
                  <Button
                    endIcon={
                      user?.source !== UserSource.SmartOnFhir || user?.organization ? (
                        anchorEl ? (
                          <ArrowDropUp />
                        ) : (
                          <ArrowDropDown />
                        )
                      ) : null
                    }
                    onClick={handleClick}
                    size="large"
                    aria-label="More options">
                    {user && `${user.firstName} ${user.lastName}`}
                  </Button>
                </DisplayDesktop>
              </Stack>
              {(user?.source !== UserSource.SmartOnFhir || user?.organization) && (
                <Menu
                  anchorEl={anchorEl}
                  id="account-menu"
                  open={open}
                  onClose={handleClose}
                  onClick={handleClose}
                  transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                  anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}>
                  {showProfile && user?.source !== UserSource.SmartOnFhir && (
                    <MenuItem onClick={handleEdit}>
                      <ListItemIcon>
                        <PersonOutline />{' '}
                      </ListItemIcon>
                      My Directory Profile
                    </MenuItem>
                  )}
                  {user && (
                    <Box>
                      {user.organization && (
                        <Box sx={{ borderBottom: '1px solid #EEEEEE', pb: 1, mb: 1 }}>
                          <QuickLinkMenuItem>
                            <ListItemIcon>
                              <HospitalIcon fontSize="small" />
                            </ListItemIcon>
                            <QuickLink
                              href={`${AppConfig.baseSelfServeUrl}/#/account`}
                              target="_blank">
                              <Stack direction="row" spacing={1} alignItems="center">
                                {userOrg && userOrg.length > 1 && (
                                  <Typography variant="body2">
                                    {userOrg[0]}
                                    <br />
                                    {userOrg[1]}
                                  </Typography>
                                )}
                                {userOrg && userOrg.length === 1 && (
                                  <Typography variant="body2">{userOrg[0]}</Typography>
                                )}
                              </Stack>
                            </QuickLink>
                          </QuickLinkMenuItem>
                        </Box>
                      )}
                      {user?.source !== UserSource.SmartOnFhir && (
                        <>
                          {managedProfilesPolicy?.canManageProfiles && (
                            <QuickLinkMenuItem>
                              <ListItemIcon>
                                <PeopleGroup fontSize="small" />
                              </ListItemIcon>
                              <QuickLink
                                href={`${AppConfig.baseSelfServeUrl}/#/manageDirectoryProfiles`}
                                target="_blank">
                                <Stack direction="row" spacing={1} alignItems="center">
                                  <Typography variant="body2">
                                    {t('Case.menu.manageDirectoryProfiles')}
                                  </Typography>
                                  {actionRequired && <NotificationDot />}
                                </Stack>
                              </QuickLink>
                            </QuickLinkMenuItem>
                          )}
                          {!managedProfilesPolicy?.canManageProfiles &&
                            managedProfilesPolicy?.canEditSelfProfile && (
                              <QuickLinkMenuItem>
                                <ListItemIcon>
                                  <UserPen fontSize="small" />
                                </ListItemIcon>
                                <QuickLink
                                  href={`${AppConfig.baseHubUrl}/#/profile/people/me`}
                                  target="_blank">
                                  <Stack direction="row">
                                    <Typography variant="body2">
                                      {t('Case.menu.manageDirectoryProfile')}
                                    </Typography>
                                    {suggestPendingCount &&
                                      typeof suggestPendingCount.count === 'number' &&
                                      suggestPendingCount.count > 0 && (
                                        <NotificationCount>
                                          <Typography variant="body5">
                                            {suggestPendingCount?.count}
                                          </Typography>
                                        </NotificationCount>
                                      )}
                                  </Stack>
                                </QuickLink>
                              </QuickLinkMenuItem>
                            )}
                          <QuickLinkMenuItem>
                            <ListItemIcon>
                              <SquareSlidersVertical fontSize="small" />
                            </ListItemIcon>
                            <QuickLink
                              href="http://info.otn.ca/email-preferences-member.html"
                              target="_blank">
                              <Typography variant="body2">
                                {t('Case.menu.communicationPreferences')}
                              </Typography>
                            </QuickLink>
                          </QuickLinkMenuItem>
                          <QuickLinkMenuItem>
                            <ListItemIcon>
                              <Cog fontSize="small" />
                            </ListItemIcon>
                            <QuickLink
                              href={`${AppConfig.baseSelfServeUrl}/#/account`}
                              target="_blank">
                              <Typography variant="body2">
                                {t('Case.menu.accountSettings')}
                              </Typography>
                            </QuickLink>
                          </QuickLinkMenuItem>
                          <QuickLinkMenuItem>
                            <ListItemIcon>
                              <SquareCheck fontSize="small" />
                            </ListItemIcon>
                            <QuickLink
                              href={`${AppConfig.baseSelfServeUrl}/#/serviceSettings`}
                              target="_blank">
                              <Typography variant="body2">
                                {t('Case.menu.serviceSettings')}
                              </Typography>
                            </QuickLink>
                          </QuickLinkMenuItem>
                          {/* <QuickLinkMenuItem>
                      <ListItemIcon>
                        <FileWaveform fontSize="small" />
                      </ListItemIcon>
                      <QuickLink href={`${AppConfig.baseHubUrl}/reports.html`} target="_blank">
                        <Typography variant="body2">
                          {t('Case.menu.serviceUtilizationReports')}
                        </Typography>
                      </QuickLink>
                    </QuickLinkMenuItem> */}
                          {(user?.roles?.orgPrimaryContact || user?.roles?.provisioning) && (
                            <QuickLinkMenuItem>
                              <ListItemIcon>
                                <UsersGear fontSize="small" />
                              </ListItemIcon>
                              <QuickLink
                                href={`${AppConfig.baseSelfServeUrl}/#/manageUsers/manageActiveUsers`}
                                target="_blank">
                                <Typography variant="body2">
                                  {t('Case.menu.manageUsers')}
                                </Typography>
                              </QuickLink>
                            </QuickLinkMenuItem>
                          )}
                          {hasEconsultDashboardAccess && (
                            <QuickLinkMenuItem>
                              <ListItemIcon>
                                <FileMagnifyingGlass fontSize="small" />
                              </ListItemIcon>
                              <QuickLink
                                href={`${AppConfig.baseSelfServeUrl}/#/productSupport/eConsult`}
                                target="_blank">
                                <Typography variant="body2">
                                  {t('Case.menu.caseStateLookup')}
                                </Typography>
                              </QuickLink>
                            </QuickLinkMenuItem>
                          )}
                          <Box sx={{ borderTop: '1px solid #EEEEEE', pt: 1, mt: 1 }}>
                            <QuickLinkMenuItem onClick={handleLogout}>
                              <ListItemIcon>
                                <Logout fontSize="small" />
                              </ListItemIcon>
                              <QuickLink>
                                <Typography variant="body2">{t('logout')}</Typography>
                              </QuickLink>
                            </QuickLinkMenuItem>
                          </Box>
                        </>
                      )}
                    </Box>
                  )}
                  {!user && (
                    <MenuItem onClick={handleClose}>
                      <ListItemIcon>
                        <Login fontSize="small" />
                      </ListItemIcon>
                      Login
                    </MenuItem>
                  )}
                </Menu>
              )}
            </ResponsiveFlexContainer>
          </Toolbar>
        </NavBarPaddedContainer>
        <Box style={alertVisibility}>
          <Alert
            sx={{
              '& .MuiSvgIcon-root:not(.MuiSvgIcon-root ~ .MuiSvgIcon-root)': {
                color: '#8a6d3b'
              },
              '& .MuiSvgIcon-root + .MuiSvgIcon-root': {
                color: '#8a6d3b'
              },
              '& .MuiAlert-message': {
                color: '#8a6d3b'
              }
            }}
            severity="warning"
            onClose={() => {
              setAlertVisibility(invisibleStyle);
            }}>
            <strong>{t('TopNavBar.unavailable.on.warning')}</strong>
            <br />
            {t('TopNavBar.unavailable.on')}
          </Alert>
        </Box>
      </NavBar>
      <EconsultCompleteCaseDialog
        isOpen={isCaseCompleteDialogOpen}
        onClose={onCompleteDialogCancelled}
        onComplete={onCompleteDialogCompleted}
      />
      <EconsultDraftLostWarningDialog
        isOpen={isDraftWarningDialogOpen}
        onLeave={onDraftWarningDialogCancelled}
        onStay={onDraftWarningDialogConfirmed}
      />
      <RenameFavouriteDialog
        isOpen={Boolean(favToRenameId)}
        favouriteId={favToRenameId || 0}
        currentDisplayName={favToRenameDisplayName || ''}
        currentFavouriteName={favToRenameFavouriteName || ''}
        onClose={handleRenameFavDialogClosed}
      />
      <RemoveFavouriteDialog
        isOpen={Boolean(favToRemoveId)}
        favouriteId={favToRemoveId || 0}
        currentFavouriteName={favToRemoveFavouriteName || ''}
        onClose={handleRemoveFavDialogClosed}
      />
    </>
  );
};

EconsultTopNavigation.defaultProps = {
  user: null
};

export default EconsultTopNavigation;
